import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useListingField,
  useMediaResource,
  useSyncedMediaResource,
} from '@brightmls/nextgen.lae.flc';
import {
  ListingResource,
  MediaField,
  PropertyField,
} from '../../../../system/field';
import {
  Alert,
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PhotoTile from '../PhotoTile';
import AddIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useService } from '../../../../contexts/ServiceContext';
import BulkDeleteConfirmModal from '../BulkDeleteConfirmModal';
import { useDropzone } from 'react-dropzone';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  TouchSensor,
} from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { MediaMetadata, useMediaUpload } from '../hooks/useMediaUpload';
import UploadDialog from '../UploadDialog';
import {
  MediaCategory,
  MediaPollingOptions,
  MediaResource,
  MediaType,
} from '../../../../system/media';
import { useMediaValidation } from '../hooks/useMediaValidation';
import { useAI } from '../hooks/useAI';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../../flags';
import {
  PreferenceKey,
  usePreferences,
} from '../../../../hooks/usePreferences';
import { useResoResource } from '../../../../reso/useResoResource';
import { MediaResource as ResoMediaResource } from '../../../../reso/resources/MediaResource';
import { AI_DESCRIPTION_LENGTH } from '../../../../config';
import { useMediaLogger } from '../hooks/useMediaLogger';
import { getBaseStyle } from '../utils/baseStyles';
import { ObjectDetectionPanel } from '../ObjectDetectionPanel';
import { FlattenedResults, useAIObjects } from '../hooks/useAIObjects';
import { MediaCloudIntegration } from '../MediaCloudIntegration';
import { useIntegrationUpload } from '../hooks/useIntegrationUpload';
import { createPortal } from 'react-dom';
import { useDrag } from '../hooks/useDrag';
import { BatchProcessedImageData } from '../ImageEditDialog/index';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { MultiDragOverlay } from '../MultiDragOverlay';
import { DraggedPhotoOverlay } from '../DraggedPhotoOverlay';
import { generateDataTestId } from 'src/utils';
import { ResoMediaDialog } from '../ResoMediaDialog';
import { useResoMedia } from '../hooks/useResoMedia';
import { PhotoActions } from './component/PhotoActions';
import InlineNote from 'src/components/common/InlineNote';
import WarningIcon from '@mui/icons-material/Warning';
import { autoHideMessage } from './constants';

// Types
export interface PhotoFile {
  MEDIA_CATEGORY: string;
  MEDIA_URL: string;
  MEDIA_DISPLAY_ORDER: number;
  MEDIA_ITEM_NUMBER: number;
  MEDIA_LONG_DESCRIPTION?: string;
  MEDIA_SOURCE_FILE_NAME?: string;
  MEDIA_SHORT_DESCRIPTION?: string;
  MEDIA_IMAGE_WIDTH: number;
  MEDIA_IMAGE_HEIGHT: number;
  MEDIA_MODIFICATION_TIMESTAMP: string;
  MEDIA_URL_MEDIUM?: string;
  MED_VW_KEY: string;
  MEDIA_URL_THUMB: string;
  MEDIA_KEY: string;
}

// Utility functions for validation
const isValidUrl = (url: any): boolean => {
  return (
    typeof url === 'string' &&
    (url.startsWith('http://') || url.startsWith('https://'))
  );
};

const isValidMedia = (media: any): boolean => {
  if (!media) return false;

  if (media.MEDIA_KEY === null) {
    console.error(`MEDIA_KEY is null for media: ${JSON.stringify(media)}`);
    throw new Error(`MEDIA_KEY is null for media: ${JSON.stringify(media)}`);
  }

  // Basic validation
  if (!media.MED_VW_KEY || !media.MEDIA_CATEGORY) return false;

  // For photos, validate URLs
  if (media.MEDIA_CATEGORY === 'Photo') {
    // Check main URL
    const hasValidUrl = isValidUrl(media.MEDIA_URL);

    // Check thumbnail URL
    const hasValidThumb = isValidUrl(media.MEDIA_URL_THUMB);

    // Check medium URL (optional for backward compatibility)
    const hasValidMedium =
      !media.MEDIA_URL_MEDIUM || isValidUrl(media.MEDIA_URL_MEDIUM);

    if (!hasValidUrl || !hasValidThumb || !hasValidMedium) {
      return false;
    }

    return true;
  }

  // For other media types, basic validation is sufficient
  return true;
};

const DEFAULT_PHOTO_POLLING_OPTIONS: MediaPollingOptions = {
  pollInterval: 500,
  maxPollAttempts: 120,
  maxShadowFailures: 5,
};

const Photos: React.FC = () => {
  // Initialize logger with component name
  const logger = useMediaLogger('AddPhotos');

  const { hasFeature, getFeatureValue } = useFeatureFlags();
  const useCloudUploaders = hasFeature(FeatureFlags.NGW_MEDIA_CLOUD_UPLOADS);
  const hasHideMediaOffMarket = hasFeature(
    FeatureFlags.NGW_HIDE_MEDIA_OFF_MARKET,
  );
  const isEditImageEnabled = hasFeature(FeatureFlags.NGW_EDIT_IMAGE);
  const isResoMediaHandlingEnabled = hasFeature(
    FeatureFlags.NGW_MEDIA_FAILURE_MODAL,
  );
  const showAIDescriptions = hasFeature(FeatureFlags.NGW_AI_IMAGE_DESCRIPTIONS);
  const useObjectDetection = hasFeature(FeatureFlags.NGW_OBJECT_DETECTION);
  const isMultiDragEnabled = hasFeature(FeatureFlags.NGW_MULTI_DRAG_DROP);

  const theme = useTheme();
  const baseStyle = getBaseStyle(theme);
  const isSmallDisplay = useMediaQuery(theme.breakpoints.down('md'));

  // Use FLC hooks for field access
  const listingKeyField = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_KEY,
    '',
  );
  const propertyTypeField = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    '',
  );
  const editKeyField = useListingField(
    ListingResource.Property,
    PropertyField.EDIT_KEY,
    '',
  );
  const countyField = useListingField(
    ListingResource.Property,
    PropertyField.COUNTY,
    '',
  );
  const visibleOffMarketKeysField = useListingField(
    ListingResource.Property,
    PropertyField.VISIBLE_WHEN_OFF_MARKET_MEDIA_KEYS,
    [] as number[],
  );
  const autoHideField = useListingField(
    ListingResource.Property,
    PropertyField.AUTOMATICALLY_HIDE_MEDIA_OFF_MARKET_YN,
    false,
  );

  const listingKey = listingKeyField.value;
  const visibleOffMarketKeys = visibleOffMarketKeysField.value;
  const autoHide = autoHideField.value;

  const { validateUploadSet } = useMediaValidation();
  const { deleteListingResources } = useService();
  const { usePreference } = usePreferences();
  const { resourceManager } = useResoResource<ResoMediaResource>(
    ListingResource.Media,
  );

  // Use specialized media hooks from FLC
  const { media: allContextMedia, updateMediaByCategory } = useMediaResource();
  const {
    items: localFiles,
    updateMedia: setLocalFiles,
    displayItems: displayFiles,
  } = useSyncedMediaResource(MediaCategory.Photo);

  // State management
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [isDeletingPhotos, setIsDeletingPhotos] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [isObjectPanelOpen, setIsObjectPanelOpen] = useState(false);
  const [objectDetectionResults, setObjectDetectionResults] =
    useState<FlattenedResults>({});

  // RESO media error handling
  const [showResoMediaDialog, setShowResoMediaDialog] = useState(false);

  const { fetchResoMedia, isFetching } = useResoMedia({
    listingKey,
    logger,
    onFetchComplete: () => {
      setShowResoMediaDialog(false);
      // Show success message
      logger.log('Photo media fetched successfully from RESO server');
    },
    onFetchError: (error) => {
      logger.error('Failed to fetch photo media from RESO:', error);
      // Keep dialog open but show error
    },
  });

  const handleMimeTypeError = useCallback(
    (error: Error, operation: string) => {
      logger.error(`MimeType null error detected during ${operation}:`, error);
      setShowResoMediaDialog(true);
    },
    [logger],
  );

  // Use the preferences hook for AI descriptions enabled state
  const [isShowDescriptions, setIsShowDescriptions] = usePreference<boolean>(
    PreferenceKey.SHOW_DESCRIPTIONS,
    true, // default value: enabled
  );

  // Use the preferences hook for AI descriptions enabled state
  const [aiDescriptionsEnabled, setAIDescriptionsEnabled] =
    usePreference<boolean>(
      PreferenceKey.AI_DESCRIPTIONS_ENABLED,
      true, // default value: enabled
    );

  // Only do AI descriptions if OPT in is checked AND flagsmith is enabled
  const useAIDescriptions = aiDescriptionsEnabled && showAIDescriptions;

  // Keep track of media that has been processed to avoid duplicate AI descriptions
  const processedMediaRef = useRef(new Set<string>());

  // Add periodic validation check ref to avoid running during sensitive operations
  const skipValidationRef = useRef<boolean>(false);

  // Track last clicked photo for Shift+Click range selection
  const lastClickedIndexRef = useRef<number | null>(null);

  const handleSaveProcessedImages = async (
    batchData: BatchProcessedImageData,
  ) => {
    if (batchData.totalProcessed === 0) {
      return;
    }

    if (batchData.totalProcessed > 0) {
      try {
        const replaceResult = await replaceMediaBatch(batchData);

        if (replaceResult.failureCount > 0) {
          console.error(
            `Failed to replace ${replaceResult.failureCount} images`,
          );
        }
      } catch (error) {
        console.error('Batch replacement failed:', error);
      }
    }
  };

  const finalMediaSync = useCallback(async () => {
    try {
      // Skip if no listing key
      if (!listingKey) {
        logger.warn('No listing key available for final media sync');
        return;
      }

      logger.log('Performing final media sync');

      // Use the ResourceManager to fetch the latest data
      const latestMedia = await resourceManager.cwa().list(listingKey);

      // Validate that we got a valid response with data
      if (!Array.isArray(latestMedia)) {
        logger.error(
          'Final media sync returned non-array result:',
          latestMedia,
        );
        return;
      }

      // Log how many items we got
      logger.log(`Fetched ${latestMedia.length} media items from RESO API`);

      // Filter out any invalid items
      const validMedia = latestMedia.filter((media) => isValidMedia(media));

      if (validMedia.length !== latestMedia.length) {
        logger.warn(
          `Filtered out ${latestMedia.length - validMedia.length} invalid media items`,
        );
      }

      // Convert resources to the format expected by the context
      const contextMedia = validMedia
        .filter((record) => record.MEDIA_CATEGORY === 'Photo')
        .map((media) => ({
          ...media,
        }));

      // Check if we have any photos
      if (contextMedia.length === 0) {
        logger.warn('No photo media found during final sync');

        // Get current photos from local state to prevent data loss
        if (localFiles.length > 0) {
          logger.log(
            `Preserving ${localFiles.length} local photos instead of empty result from API`,
          );

          // Keep the local files and skip the update
          return;
        }
      }

      // Log what we're updating to
      logger.log(`Updating media context with ${contextMedia.length} photos`);

      // Update only the context, not the local state
      updateMediaByCategory({
        category: MediaCategory.Photo,
        resources: contextMedia as MediaResource[],
      });
    } catch (error) {
      logger.error('Error during final media sync:', error);

      // Show sync error to user
      setSyncError('Error syncing media. Please refresh the page.');
    }
  }, [listingKey, updateMediaByCategory, localFiles, resourceManager, logger]);

  const objectMediaSync = async (results: FlattenedResults) => {
    const mergedResults = { ...objectDetectionResults, ...results };
    setObjectDetectionResults(mergedResults);
  };

  const maxDescriptionLength = Number(
    getFeatureValue(FeatureFlags.NGW_AI_IMAGE_DESCRIPTIONS, true) ||
      AI_DESCRIPTION_LENGTH,
  );

  const mediaPollingOptions: MediaPollingOptions =
    getFeatureValue(FeatureFlags.NGW_MEDIA_POLLING) ||
    DEFAULT_PHOTO_POLLING_OPTIONS;

  const {
    generateBatchDescriptions,
    cancelGeneration,
    isGeneratingDescription,
    isPartOfCurrentBatchGenerating,
  } = useAI({
    listingKey,
    onComplete: finalMediaSync,
    maxDescriptionLength,
  });

  const {
    generateBatchObjects,
    isPendingGeneration,
    pendingGenerations,
    progress,
  } = useAIObjects({ listingKey, onComplete: objectMediaSync });

  const {
    isDraggingMultiple,
    draggedItems,
    draggedItemId,
    isOverMainPosition,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    getValidDropTargets,
  } = useDrag({
    selectedFiles,
    skipValidationRef,
    setSelectedFiles,
  });

  const pendingDescriptionMedia = useRef<Array<{ key: string; url: string }>>(
    [],
  );

  const pendingObjectMedia = useRef<Array<{ key: string; url: string }>>([]);

  const isAnyAIGenerationActive = useCallback(() => {
    // Check if any of the current files are having descriptions generated
    return displayFiles.some(
      (file) =>
        isGeneratingDescription(file.MED_VW_KEY) ||
        (!file.MEDIA_SHORT_DESCRIPTION &&
          isPartOfCurrentBatchGenerating(file.MED_VW_KEY)),
    );
  }, [displayFiles, isGeneratingDescription, isPartOfCurrentBatchGenerating]);

  // Create a memoized state to avoid recalculating this on every render
  const isSortingDisabled = useMemo(() => {
    return isAnyAIGenerationActive();
  }, [isAnyAIGenerationActive]);

  // Add a ref to track pending uploads
  const isUploadingRef = useRef(false);
  const pendingUploadsRef = useRef(new Set<number>());

  // Modify the upload completion handler
  const handleUploadComplete = useCallback(
    (result: any) => {
      logger.log('handleUploadComplete called:', result);
      const newMediaFile = result;
      pendingUploadsRef.current.delete(newMediaFile.MEDIA_ITEM_NUMBER);

      // Validate the media before adding it to the state
      if (!isValidMedia(newMediaFile)) {
        logger.group(() => {
          logger.error(
            `Detected invalid media after upload: ${newMediaFile.MEDIA_ITEM_NUMBER}`,
          );
          logger.error('Invalid media details:', newMediaFile);
        });
        return;
      }

      // Start post-processing for images
      if (
        !processedMediaRef.current.has(newMediaFile.MED_VW_KEY) &&
        isValidMedia(newMediaFile) // Only add valid media for AI processing
      ) {
        processedMediaRef.current.add(newMediaFile.MED_VW_KEY); // Mark as processed right away
        if (useAIDescriptions) {
          // Queue up ai descriptions
          pendingDescriptionMedia.current.push({
            key: newMediaFile.MED_VW_KEY,
            url: newMediaFile.MEDIA_URL_MEDIUM,
          });
          logger.log(
            `Added media to AI description queue: ${newMediaFile.MED_VW_KEY}`,
          );
        }
        if (useObjectDetection) {
          // Queue up ai object detection
          pendingObjectMedia.current.push({
            key: newMediaFile.MED_VW_KEY,
            url: newMediaFile.MEDIA_URL_MEDIUM,
          });
          logger.log(
            `Added media to AI object queue: ${newMediaFile.MED_VW_KEY}`,
          );
        }
      }

      setLocalFiles((prevFiles) => {
        const newFiles = [...prevFiles, newMediaFile];
        logger.log(
          `Updated local files, pendingUploads: ${pendingUploadsRef.current.size}`,
        );
        return newFiles;
      });
    },
    [
      generateBatchDescriptions,
      allContextMedia,
      aiDescriptionsEnabled,
      listingKey,
      logger,
      localFiles,
    ],
  );

  const handleSortByName = useCallback(() => {
    // Set skipValidation flag to prevent interruptions
    skipValidationRef.current = true;

    try {
      logger.log('Sorting photos by filename');
      // Get the current photo files
      const photoFiles = localFiles;

      // Find the main photo (the one with display order 0)
      const mainPhotoIndex = photoFiles.findIndex(
        (file: MediaResource) => file.MEDIA_DISPLAY_ORDER === 0,
      );

      // If we found a main photo, we'll handle it separately
      let mainPhoto;
      let otherPhotos;

      if (mainPhotoIndex !== -1) {
        mainPhoto = photoFiles[mainPhotoIndex];
        otherPhotos = photoFiles.filter(
          (_: MediaResource, index: number) => index !== mainPhotoIndex,
        );
      } else {
        // No main photo found, just sort everything
        mainPhoto = null;
        otherPhotos = [...photoFiles];
      }

      // Sort the other files by name
      const sortedOtherPhotos = otherPhotos.sort((a: any, b: any) =>
        (a.MEDIA_SOURCE_FILE_NAME || '').localeCompare(
          b.MEDIA_SOURCE_FILE_NAME || '',
        ),
      );

      // Combine the main photo with the sorted ones
      let sortedFiles;
      if (mainPhoto) {
        sortedFiles = [mainPhoto, ...sortedOtherPhotos];
      } else {
        sortedFiles = sortedOtherPhotos;
      }

      // Update display orders while preserving all other properties
      const updatedFiles = sortedFiles.map(
        (file: MediaResource, index: number) => ({
          ...file,
          MEDIA_DISPLAY_ORDER: index,
        }),
      );

      updatedFiles.forEach((file: any) => {
        if (file.MEDIA_KEY === null) {
          console.error(
            `MEDIA_KEY is null for sortedFiles: ${JSON.stringify(file)}`,
          );
          throw new Error(
            `MEDIA_KEY is null for sortedFiles: ${JSON.stringify(file)}`,
          );
        }
      });

      // Store as last known good state
      setLocalFiles(updatedFiles);

      logger.log(`Sorted ${updatedFiles.length} photos by name`);
    } catch (error) {
      logger.error('Error sorting photos by name:', error);
    } finally {
      // Reset skipValidation flag after operation completes
      setTimeout(() => {
        skipValidationRef.current = false;
      }, 500);
    }
  }, [localFiles, logger]);

  // Media upload hook configuration
  const {
    uploadMediaBatch,
    replaceMediaBatch,
    isUploading,
    uploadProgress,
    isBatchPollingActive,
    startProcessingFiles,
  } = useMediaUpload({
    listingKey,
    listingDetails: {
      CWAPropertyType: propertyTypeField.value,
      CWALISKey: editKeyField.value,
      CWACounty: countyField.value,
    },
    logger,
    maxShadowFailures: mediaPollingOptions.maxShadowFailures,
    pollInterval: mediaPollingOptions.pollInterval,
    maxPollAttempts: mediaPollingOptions.maxPollAttempts,
    onMimeTypeError: isResoMediaHandlingEnabled
      ? handleMimeTypeError
      : undefined,
    onReplaceComplete: (result) => {
      logger.log('onReplaceComplete called:', result);
      setLocalFiles((previousMediaList) => {
        if (!result?.MED_VW_KEY) {
          return previousMediaList;
        }

        const existingIndex = previousMediaList.findIndex(
          (item) => item.MED_VW_KEY === result.MED_VW_KEY,
        );

        if (existingIndex === -1) {
          return [...previousMediaList, result];
        }

        const previousMediaDisplayOrder =
          previousMediaList[existingIndex][MediaField.MEDIA_DISPLAY_ORDER];
        const nextMediaList = previousMediaList.slice();
        nextMediaList[existingIndex] = {
          ...result,
          [MediaField.MEDIA_DISPLAY_ORDER]: previousMediaDisplayOrder,
        };

        return nextMediaList;
      });
    },
    onReplaceError: (error, metadata) => {
      logger.log('onReplaceError called:', error, metadata);
      logger.error(`Failed to replace ${metadata.file?.name}:`, error);
    },
    onUploadComplete: handleUploadComplete,
    onBatchComplete: () => {
      logger.log('=== onBatchComplete called ===');
      logger.log(
        `Pending uploads remaining: ${pendingUploadsRef.current.size}`,
      );

      // Always run finalMediaSync first
      finalMediaSync().then(() => {
        pendingUploadsRef.current.clear();

        logger.log('Final media sync completed');

        // Now handle AI generation after sync is done
        // Start generating AI object detection for pending media if enabled
        if (useObjectDetection && pendingObjectMedia.current.length > 0) {
          logger.log(
            `Starting AI object generation for ${pendingObjectMedia.current.length} images`,
          );
          generateBatchObjects([...pendingObjectMedia.current]);
          pendingObjectMedia.current = [];
        }

        // Start generating AI descriptions for pending media if enabled
        if (useAIDescriptions && pendingDescriptionMedia.current.length > 0) {
          logger.log(
            `Starting AI description generation for ${pendingDescriptionMedia.current.length} images`,
          );
          generateBatchDescriptions([...pendingDescriptionMedia.current]);
          pendingDescriptionMedia.current = [];
        }
      });
    },
    onUploadError: (error, metadata) => {
      logger.error(`Failed to upload ${metadata.name}:`, error);
    },
  });

  useEffect(() => {
    isUploadingRef.current = isBatchPollingActive;
  }, [isBatchPollingActive]);

  const handleDrop = useCallback(
    async (files: File[]) => {
      // Set skipValidation flag to prevent interruptions
      skipValidationRef.current = true;

      try {
        logger.log(`Handling drop of ${files.length} files`);
        logger.log(`Polling options -- ${JSON.stringify(mediaPollingOptions)}`);

        const acceptedFiles = await validateUploadSet({
          files,
          type: MediaType.Photo,
          currentCount: displayFiles.length,
        });

        if (acceptedFiles.length === 0) {
          logger.warn('No files accepted after validation');
          return;
        }

        // Find the true maximum item number including any pending uploads
        const maxItemNumber = Math.max(
          ...allContextMedia.map((item: any) => item.MEDIA_ITEM_NUMBER),
          ...localFiles.map((item: any) => item.MEDIA_ITEM_NUMBER),
          0,
        );

        const baseDisplayOrder =
          Math.max(...localFiles.map((f) => f.MEDIA_DISPLAY_ORDER), -1) + 1;

        logger.log(
          `Preparing ${acceptedFiles.length} files for upload with base display order ${baseDisplayOrder}`,
        );

        // Reset tracking for new batch
        pendingUploadsRef.current = new Set();

        const preparedFiles = await Promise.all(
          acceptedFiles.map(async (file, index) => {
            // Ensure each new file gets a unique item number
            const itemNumber = maxItemNumber + index + 1;
            const metadata = {
              name: file.name,
              type: file.type,
              category: 'Photo',
              description: null,
              size: file.size,
              file,
              itemNumber,
              id: file.name, // Use name as ID for simplicit
              displayOrder: baseDisplayOrder + index,
            } as MediaMetadata;
            // Track this upload
            pendingUploadsRef.current.add(itemNumber);
            logger.log(
              `Added file to upload queue: ${file.name} (item #${itemNumber})`,
            );
            return metadata;
          }),
        );

        await uploadMediaBatch(preparedFiles);
      } catch (error) {
        logger.error('Error handling file drop:', error);
      } finally {
        // Reset skipValidation flag after operation completes
        setTimeout(() => {
          skipValidationRef.current = false;
        }, 500);
      }
    },
    [
      displayFiles.length,
      allContextMedia,
      localFiles,
      uploadMediaBatch,
      validateUploadSet,
      logger,
      useIntegrationUpload,
    ],
  );

  // Dropzone configuration
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    minSize: 0,
    onDrop: handleDrop,
  });

  const handleDeletePhoto = useCallback(
    (fileVWKey: string) => {
      // Set skipValidation flag to prevent interruptions
      skipValidationRef.current = true;

      try {
        // Get the deleted file's current display order
        const deletedFile = localFiles.find(
          (file) => file.MED_VW_KEY === fileVWKey,
        );
        if (!deletedFile) {
          logger.warn(
            `Cannot delete photo: file with key ${fileVWKey} not found`,
          );
          return;
        }

        const deletedDisplayOrder = deletedFile.MEDIA_DISPLAY_ORDER;
        logger.log(
          `Deleting photo: ${deletedFile.MEDIA_SOURCE_FILE_NAME || 'unnamed'} (key: ${fileVWKey})`,
        );

        // Filter out the deleted file and adjust display orders to maintain sequence
        const updatedFiles = localFiles
          .filter((file) => file.MED_VW_KEY !== fileVWKey)
          .map((file) => {
            if (file.MEDIA_DISPLAY_ORDER > deletedDisplayOrder) {
              // Shift display orders down by 1 for all files after the deleted one
              return {
                ...file,
                MEDIA_DISPLAY_ORDER: file.MEDIA_DISPLAY_ORDER - 1,
              };
            }
            return file;
          });

        updatedFiles.forEach((file: any) => {
          if (file.MEDIA_KEY === null) {
            console.error(
              `MEDIA_KEY is null after delete Files: ${JSON.stringify(file)}`,
            );
            throw new Error(
              `MEDIA_KEY is null after delete Files: ${JSON.stringify(file)}`,
            );
          }
        });

        // Update local state
        setLocalFiles(updatedFiles);

        // Clear selection if the deleted file was selected
        setSelectedFiles((prev) => prev.filter((key) => key !== fileVWKey));

        // Delete from the backend
        deleteListingResources(parseInt(listingKey || ''), [
          {
            type: 'Media',
            itemKey: parseInt(fileVWKey),
          },
        ]).catch((error) => {
          logger.error('Failed to delete photo from backend:', error);
        });
      } catch (error) {
        logger.error(`Error deleting photo ${fileVWKey}:`, error);
      } finally {
        // Reset skipValidation flag after operation completes
        setTimeout(() => {
          skipValidationRef.current = false;
        }, 500);
      }
    },
    [deleteListingResources, listingKey, localFiles, logger],
  );

  // Enhanced Select All / Deselect All handling
  const handleSelectAll = useCallback(() => {
    if (selectedFiles.length === localFiles.length) {
      // Deselecting all
      logger.log('Deselecting all photos');
      setSelectedFiles([]);
    } else {
      // Selecting all
      logger.log(`Selecting all ${localFiles.length} photos`);
      const allKeys = localFiles.map((f) => f.MED_VW_KEY);
      setSelectedFiles(allKeys);
    }
  }, [localFiles, selectedFiles, logger]);

  const handleBulkDelete = useCallback(async () => {
    // Set skipValidation flag to prevent interruptions
    skipValidationRef.current = true;

    try {
      // Validate that we have files to delete
      if (selectedFiles.length === 0) {
        logger.warn('No files selected for bulk delete');
        setIsDeleteModalOpen(false);
        return;
      }

      setIsDeleteModalOpen(false);
      setIsDeletingPhotos(true);

      logger.log(`Starting bulk delete of ${selectedFiles.length} photos`);

      // Keep a copy of the current files for rollback if needed
      const filesCopy = [...localFiles];

      // Get all display orders of files being deleted
      const deletedDisplayOrders = new Set(
        localFiles
          .filter((file) => selectedFiles.includes(file.MED_VW_KEY))
          .map((file) => file.MEDIA_DISPLAY_ORDER),
      );

      // Update local state first for immediate UI feedback
      const remainingFiles = localFiles
        .filter((file) => !selectedFiles.includes(file.MED_VW_KEY))
        .map((file) => {
          // Count how many deleted files had lower display orders
          const lowerOrderedDeletions = Array.from(deletedDisplayOrders).filter(
            (order) => order < file.MEDIA_DISPLAY_ORDER,
          ).length;

          // Shift the display order down by the number of lower-ordered deletions
          return {
            ...file,
            MEDIA_DISPLAY_ORDER:
              file.MEDIA_DISPLAY_ORDER - lowerOrderedDeletions,
          };
        });

      remainingFiles.forEach((file: any) => {
        if (file.MEDIA_KEY === null) {
          console.error(
            `MEDIA_KEY is null after bulk delete Files: ${JSON.stringify(file)}`,
          );
          throw new Error(
            `MEDIA_KEY is null after bulk delete Files: ${JSON.stringify(file)}`,
          );
        }
      });

      // if no remaining files match the MEDIA_KEY saved for Keep When Closed, clear the value
      if (
        !remainingFiles.find(
          (file: MediaResource) => file.MEDIA_KEY === visibleOffMarketKeys[0],
        )
      ) {
        visibleOffMarketKeysField.updateValue([]);
      }
      setLocalFiles(remainingFiles);

      // Clear the selection state
      setSelectedFiles([]);

      try {
        // Delete the selected files from the backend
        logger.log(`Deleting ${selectedFiles.length} photos from backend`);
        await deleteListingResources(
          parseInt(listingKey || ''),
          selectedFiles.map((key) => ({
            type: 'Media',
            itemKey: parseInt(key),
          })),
        );

        logger.log('Backend delete successful');
      } catch (error) {
        logger.error('Failed to delete selected photos from backend:', error);

        // Rollback local state on backend error
        logger.log('Rolling back to previous state due to backend error');
        setLocalFiles(filesCopy);

        // Show error to user
        setSyncError('Failed to delete photos. Please try again.');
      }
    } catch (error) {
      logger.error('Failed to delete selected photos:', error);
      setSyncError('An error occurred while deleting photos.');
    } finally {
      setIsDeletingPhotos(false);

      // Reset skipValidation flag after operation completes
      setTimeout(() => {
        skipValidationRef.current = false;
      }, 500);
    }
  }, [deleteListingResources, listingKey, localFiles, selectedFiles, logger]);

  const handleReanalyzePhotos = useCallback(() => {
    logger.log('Starting re-analysis of photos');

    // Clear existing results
    setObjectDetectionResults({});

    // Clear localStorage
    if (listingKey) {
      try {
        localStorage.removeItem(`objectDetection_${listingKey}`);
        logger.log('Cleared localStorage for object detection');
      } catch (error) {
        logger.error('Failed to clear localStorage:', error);
      }
    }

    // Prepare media items for analysis - filter out any with undefined URLs
    const mediaItems = localFiles
      .filter((file) => file.MEDIA_URL_MEDIUM)
      .map((file) => ({
        key: file.MED_VW_KEY,
        url: file.MEDIA_URL_MEDIUM as string,
      }));

    // Start batch processing
    if (mediaItems.length > 0) {
      logger.log(`Re-analyzing ${mediaItems.length} photos`);
      generateBatchObjects(mediaItems);
    } else {
      logger.warn('No photos available to re-analyze');
    }
  }, [listingKey, localFiles, generateBatchObjects, logger]);

  // DnD handlers
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 50,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 50,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const { handleIntegrationUpload } = useIntegrationUpload({
    displayFilesLength: displayFiles.length,
    allContextMedia,
    localFiles,
    uploadMediaBatch,
    pendingUploadsRef,
    category: 'Photo',
    onFilesReceived: handleDrop,
    onProcessingStart: startProcessingFiles,
  });

  const onAddPhotoToSelected = (e: any) => {
    const key = e.currentTarget.getAttribute('photo-key');
    if (!key) return;

    // Get the index of the clicked photo
    const clickedIndex = displayFiles.findIndex(
      (file) => file.MED_VW_KEY === key,
    );

    if (clickedIndex === -1) return;

    // Check if Shift key is pressed for range selection
    if (e.shiftKey && lastClickedIndexRef.current !== null) {
      // Range selection: select all photos between last clicked and current
      const startIndex = Math.min(lastClickedIndexRef.current, clickedIndex);
      const endIndex = Math.max(lastClickedIndexRef.current, clickedIndex);

      // Get all photo keys in the range
      const rangeKeys = displayFiles
        .slice(startIndex, endIndex + 1)
        .map((file) => file.MED_VW_KEY);

      setSelectedFiles((prev) => {
        // Add all range keys to selection (union)
        const newSelection = new Set([...prev, ...rangeKeys]);
        return Array.from(newSelection);
      });
    } else {
      // Normal click: toggle selection
      setSelectedFiles((prev) => {
        if (prev.includes(key)) {
          // Deselecting: remove from selection
          return prev.filter((k) => k !== key);
        } else {
          // Selecting: add to selection
          return [...prev, key];
        }
      });
    }

    // Update last clicked index for future Shift+Click operations
    lastClickedIndexRef.current = clickedIndex;
  };

  const isKeepWhenClosed = useCallback(
    (file: MediaResource) => {
      return (
        visibleOffMarketKeys.length > 0 &&
        visibleOffMarketKeys[0] === Number(file.MEDIA_KEY)
      );
    },
    [visibleOffMarketKeys],
  );

  const renderPhotoTiles = () => {
    const filesToRender = getValidDropTargets(displayFiles);
    return filesToRender.map((file, index) => {
      return (
        <PhotoTile
          key={file.MED_VW_KEY}
          isDragging={draggedItemId === file.MEDIA_ITEM_NUMBER.toString()}
          isKeepWhenClosed={isKeepWhenClosed(file)}
          isSortingDisabled={isSortingDisabled}
          isSmallDisplay={isSmallDisplay}
          listingKey={listingKey}
          isPrimary={index === 0}
          isOverMainPosition={isOverMainPosition}
          file={file}
          updateOffMarketKeys={visibleOffMarketKeysField.updateValue}
          onDeletePhoto={handleDeletePhoto}
          selectedFiles={selectedFiles}
          isShowDescriptions={isShowDescriptions}
          isGeneratingDescription={isGeneratingDescription}
          isPartOfCurrentBatchGenerating={isPartOfCurrentBatchGenerating}
          cancelGeneration={cancelGeneration}
          onAddPhotoToSelected={onAddPhotoToSelected}
        />
      );
    });
  };

  const renderDragOverlay = () => {
    if (isMultiDragEnabled && isDraggingMultiple && draggedItems) {
      return (
        <DragOverlay modifiers={[snapCenterToCursor]}>
          <MultiDragOverlay
            files={draggedItems?.files || []}
            isShowDescriptions={isShowDescriptions}
            isSmallDisplay={isSmallDisplay}
            cardOffset={10}
          />
        </DragOverlay>
      );
    } else if (draggedItemId) {
      return (
        <DragOverlay>
          <DraggedPhotoOverlay
            file={displayFiles.find(
              (f) => f.MEDIA_ITEM_NUMBER.toString() === draggedItemId,
            )}
            isSmallDisplay={isSmallDisplay}
            isShowDescriptions={isShowDescriptions}
          />
        </DragOverlay>
      );
    }
    return null;
  };

  return (
    <>
      <section className="container">
        <div
          style={{
            ...baseStyle,
            height: '100%',
            padding: '0px',
            justifyContent: isSmallDisplay ? 'center' : 'flex-start',
          }}
        >
          <Grid
            container
            {...getRootProps({ className: 'dropzone' })}
            width="100%"
            sx={{
              display: 'flex',
              flexDirection: isSmallDisplay ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              minHeight: '144px',
              padding: '20px 0px',
            }}
          >
            <Grid
              sx={{
                display: 'flex',
              }}
            >
              <input {...getInputProps()} />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isSmallDisplay ? 'column' : 'row',
                  justifyContent: 'center',
                  alignItems: 'center', // Center all content vertically
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pr: 4, // Spacing between icon and content
                  }}
                >
                  <AddIcon
                    fontSize="large"
                    sx={{ color: 'secondary.medium', fontSize: '48px' }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isSmallDisplay ? 'center' : 'flex-start',
                    maxWidth: '500px', // Adjust this value as needed
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      fontWeight: 'bold',
                      fontSize: '21px',
                      whiteSpace: 'pre',
                      mb: showAIDescriptions ? '5px' : 0,
                    }}
                  >
                    {!isSmallDisplay && 'Drag and drop or '}
                    <Typography
                      sx={{
                        display: 'flex',
                        pl: '2px',
                        fontWeight: 'bold',
                        fontSize: '21px',
                        color: 'secondary.medium',
                        textDecoration: 'underline',
                      }}
                    >
                      {isSmallDisplay ? 'Click' : 'click'} to browse
                    </Typography>
                  </Box>

                  {showAIDescriptions && (
                    <Box
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '-10px',
                      }}
                    >
                      <Checkbox
                        slotProps={{
                          input: {
                            'data-testid': generateDataTestId(
                              'aiDescription',
                              undefined,
                              'checkbox',
                            ),
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                          transform: 'scale(1.10)',
                        }}
                        className="bmls_checkbox"
                        size="medium"
                        checked={aiDescriptionsEnabled}
                        onChange={(e) =>
                          setAIDescriptionsEnabled(e.target.checked)
                        }
                      />
                      <Typography
                        sx={{
                          fontSize: { xs: '12px', sm: '14px', md: '16px' },
                          ml: 0,
                        }}
                      >
                        Suggest AI-generated photo descriptions.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
            {useCloudUploaders && (
              <>
                <Divider
                  orientation={isSmallDisplay ? 'horizontal' : 'vertical'}
                  variant="middle"
                  sx={{
                    borderWidth: 1,
                    height: isSmallDisplay ? '1px' : '70px',
                    margin: isSmallDisplay ? '20px 0' : '0',
                    width: isSmallDisplay ? '80%' : '1px',
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                  }}
                />
                <MediaCloudIntegration
                  props={{
                    handleUrlUpload: handleIntegrationUpload,
                    onFilesReceived: handleDrop,
                    onProcessingStart: startProcessingFiles,
                  }}
                />
              </>
            )}
          </Grid>
        </div>
      </section>

      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontSize: '.8rem', color: 'grey.400' }}>
          Supported formats: JPG, and JPEG
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { sm: 'row' },
            justifyContent: isSmallDisplay ? 'flex-start' : 'space-between',
          }}
        >
          <Typography sx={{ fontSize: '.8rem', color: 'grey.400' }}>
            Max size: 20mb
          </Typography>
          <Divider
            orientation="vertical"
            sx={{ mx: 2, height: '14px', mb: '2px', mt: '2px' }}
          />
          <Typography sx={{ fontSize: '.8rem', color: 'grey.400' }}>
            Min width: 1024px recommended.
          </Typography>
        </Box>
      </Box>

      {localFiles.length > 0 ? (
        <Box>
          <PhotoActions
            localFiles={localFiles}
            selectedFiles={selectedFiles}
            onSortByName={handleSortByName}
            onSelectAll={handleSelectAll}
            onDeleteSelected={() => setIsDeleteModalOpen(true)}
            onSaveProcessedImages={handleSaveProcessedImages}
            objectDetectionResults={objectDetectionResults}
            isPendingGeneration={isPendingGeneration}
            pendingGenerations={pendingGenerations}
            progress={progress}
            onOpenObjectPanel={() => setIsObjectPanelOpen(true)}
            onReanalyzePhotos={handleReanalyzePhotos}
            useObjectDetection={useObjectDetection}
            isEditImageEnabled={isEditImageEnabled}
            isShowDescriptions={isShowDescriptions}
            setIsShowDescriptions={setIsShowDescriptions}
            isSortingDisabled={isSortingDisabled}
            isSmallDisplay={isSmallDisplay}
          />
          {hasHideMediaOffMarket &&
            Boolean(autoHide) &&
            !visibleOffMarketKeys.length && (
              <InlineNote
                message={autoHideMessage}
                severity="warning"
                icon={WarningIcon}
                styles={{ border: '1px solid', borderColor: 'warning.main' }}
              />
            )}

          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext
              items={getValidDropTargets(displayFiles).map((o) =>
                o.MEDIA_ITEM_NUMBER.toString(),
              )}
              strategy={rectSortingStrategy}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-start',
                  width: '100%',
                  position: 'relative',
                }}
              >
                {/* Render all tiles */}
                {renderPhotoTiles()}
              </div>
            </SortableContext>

            {/* Drag Overlay - renders the dragged photo following mouse */}
            {createPortal(renderDragOverlay(), document.body)}
          </DndContext>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', my: 16 }}>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: isSmallDisplay ? '1.3rem' : '1rem',
            }}
          >
            You have no photos
          </Typography>
          <Typography>
            {isSmallDisplay ? 'Browse' : 'Drag, drop, or browse'} to upload
            photos.
          </Typography>
        </Box>
      )}

      <Backdrop
        sx={{ color: '#fff', zIndex: theme.zIndex.drawer + 1 }}
        open={isDeletingPhotos}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <UploadDialog
        open={isUploading}
        isBatchPolling={
          isBatchPollingActive || pendingUploadsRef.current.size > 0
        }
        uploadProgress={{
          totalFiles: uploadProgress.totalFiles,
          completedFiles: uploadProgress.completedFiles,
          currentFile: uploadProgress.currentFile,
          remainingFiles: uploadProgress.remainingFiles,
          failedFiles: uploadProgress.failedFiles,
        }}
      />

      <BulkDeleteConfirmModal
        mediaType="Photos"
        numberSelectedMedia={selectedFiles.length}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        onDeletePhotos={handleBulkDelete}
      />

      {/* Error notification */}
      <Snackbar
        open={!!syncError}
        autoHideDuration={6000}
        onClose={() => setSyncError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSyncError(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {syncError}
        </Alert>
      </Snackbar>

      <ObjectDetectionPanel
        isOpen={isObjectPanelOpen}
        setIsObjectPanelOpen={setIsObjectPanelOpen}
        isPendingGeneration={isPendingGeneration}
        pendingGenerations={pendingGenerations}
        progress={progress}
        listingKey={listingKey}
        objectResults={objectDetectionResults}
        setObjectDetectionResults={setObjectDetectionResults}
      />

      {isResoMediaHandlingEnabled && (
        <ResoMediaDialog
          open={showResoMediaDialog}
          onClose={() => !isFetching && setShowResoMediaDialog(false)}
          onFetchMedia={fetchResoMedia}
          isFetching={isFetching}
        />
      )}
    </>
  );
};

export default Photos;
